/* CloneAction.js */

"use strict";
const Registrations = require("../../../../../RegistrationNamespaces");
const CreateQuestionComponent = require("../create/CreateQuestionComponent");
const _ = require("lodash");

class CloneAction {
    constructor(
        logger,
        gameEditor,
        modalUserFeedback,
        NetworkDiscoveryPlan,
        userSession,
        activeView
    ) {
        this._logger = logger;
        this._gameEditor = gameEditor;
        this._activeOrgId = userSession.activeOrganization.id;
        this._NetworkDiscoveryPlanEntity = NetworkDiscoveryPlan;
        this._planEntity = new NetworkDiscoveryPlan(this._activeOrgId);
        this._modalUserFeedback = modalUserFeedback;
        this._activeView = activeView;
        this._firstRun = true;

        this.run = this.run.bind(this);
        this.cloneErrorHandler = this.cloneErrorHandler.bind(this);
    }
    run() {
        const selectedPlanId = _.get(this._activeView, "selectedRows[0]");
        if (selectedPlanId) {
            return this._NetworkDiscoveryPlanEntity
                .loadNetworkDiscoveryPlan(this._activeOrgId, selectedPlanId, {
                    name: true,
                    description: true,
                    mode: true,
                    connectionInformation: true,
                    status: true,
                    engineZoneId: true,
                    searchType: true,
                    useArp: true,
                    useCdp: true,
                    useLldp: true,
                    vrf: true,
                    connectionType: true,
                    credentials: true,
                    enablePasswords: true,
                    exclusions: true,
                    scanPort: true,
                    searchDepth: true,
                    targets: true,
                    useVrf: true,
                    zoneName: true,
                })
                .then((selectedPlan) => {
                    this._selectedPlan = selectedPlan;

                    if (_.isEmpty(selectedPlan)) {
                        return this.showCloneError({
                            errorMsg: "Cannot retrieve the selected Network Discovery Plan",
                            errorCode: "EntityRetrieveError",
                            errorTitle: "Clone Plan Error",
                        });
                    }

                    const properties = selectedPlan.properties;

                    Object.keys(properties).forEach((prop) => {
                        if (prop === "name") {
                            this._planEntity[prop] = properties[prop] + "-1";
                        } else if (prop === "status") {
                            if (this._isEntityValid()) {
                                this._planEntity[prop] = "READY";
                            } else {
                                this._planEntity[prop] = "DRAFT";
                            }
                        } else {
                            this._planEntity[prop] = properties[prop];
                        }
                    });

                    return this.clonePlan().catch(this.cloneErrorHandler);
                });
        } else {
            return this.showCloneError({
                errorMsg: "Missing selected Network Discovery Plan",
                errorCode: "MissingEntity",
                errorTitle: "Clone Plan Error",
            });
        }
    }

    cloneErrorHandler(err) {
        if (err.userRejected) {
            return this._modalUserFeedback.hideDisplayed();
        }
        this._logger.warn({error: err}, "Clone action error.");
        const errorCode = err && err.errorCode;
        switch (errorCode) {
            case "EntityAlreadyExists":
                if (this._firstRun) {
                    this._firstRun = false;
                    return this.requestPlanName(false);
                }
                return this.requestPlanName(true);
            default:
                return this.showCloneError(err);
        }
    }

    showCloneError(error) {
        return this._modalUserFeedback.displayError({error}).catch((error) => {
            if (error.userRejected) {
                return;
            }
            this._logger.warn({error}, "Clone action error.");
        });
    }

    clonePlan() {
        let newItemId;
        return this._planEntity
            .create()
            .then((result) => {
                newItemId = result.id;
                return this._modalUserFeedback.hideDisplayed().then(() => {
                    return this.launchNetworkDiscoveryEditor(result.id, result.properties.name);
                });
            })
            .then(() => {
                if (typeof this._activeView.resetSelection === "function") {
                    // making a small delay, to be sure the grid is updated with new item
                    _.delay(() => this._activeView.resetSelection([newItemId]), 200);
                }
                return Promise.resolve(this);
            });
    }

    requestPlanName(isNotUniq) {
        return this._modalUserFeedback
            .displayCustom(
                new CreateQuestionComponent(
                    this._logger,
                    "Clone Network Discovery Plan",
                    isNotUniq
                        ? "The entered name is not unique. Please specify a unique name for this cloned plan."
                        : "Please specify a unique name for this cloned plan."
                ),
                {dontHide: true}
            )
            .then((result) => {
                return this._modalUserFeedback.displayLoading().then(() => {
                    this._planEntity.name = result.trim();
                    return this.clonePlan();
                });
            })
            .catch((err) => {
                return this.cloneErrorHandler(err);
            });
    }

    launchNetworkDiscoveryEditor(id, name) {
        if (!id) {
            throw "Plan creation error, invalid plan id.";
        }
        let editorState = {
            fqn: Registrations.NETWORK_DISCOVERY_EDITOR_VIEW_DETAILS,
            viewState: {createMode: true},
        };
        let launchOpts = {id: id, title: name};
        return this._gameEditor.launchNetworkDiscoveryEditor(editorState, launchOpts);
    }

    _isEntityValid() {
        let valid = true;
        const connectionInfo = this._selectedPlan.getConnectionInformation();
        valid =
            _.isEmpty(_.trim(this._selectedPlan.getName() || "")) !== "" &&
            !_.isEmpty(this._selectedPlan.getCredentials() || []);

        if (valid && this._selectedPlan.getMode() === "CRAWL") {
            valid =
                !_.isEmpty(connectionInfo.ip) &&
                this._validateIp(_.trim(connectionInfo.ip)).valid &&
                !_.isEmpty(connectionInfo.type) &&
                _.inRange(connectionInfo.port, 1, 99) &&
                !_.isEmpty(connectionInfo.credentials) &&
                this._oneOfTheSearchMethodsIsTrue();
        } else if (valid && this._selectedPlan.getMode() === "SCAN") {
            valid = !_.isEmpty(this._selectedPlan.getTargets() || []);
        }

        if (valid) {
            this._isPlanValid = true;
        } else {
            this._isPlanValid = false;
        }

        return valid;
    }

    _oneOfTheSearchMethodsIsTrue() {
        return (
            this._selectedPlan.getUseArp() ||
            false ||
            this._selectedPlan.getUseCdp() ||
            false ||
            this._selectedPlan.getUseLldp() ||
            false
        );
    }

    _validateIp(val) {
        let ipType;
        const v4expression =
            /^(?:(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.){3}(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)$/;
        if (v4expression.test(val)) {
            ipType = 4;
            return {
                ipType,
                valid: true,
            };
        }
        const v6expression = /^([0-9A-Fa-f]{1,4}::?){1,7}[0-9A-Fa-f]{1,4}$/;
        if (v6expression.test(val)) {
            ipType = 6;
            return {
                ipType,
                valid: true,
            };
        }
        return {
            ipType,
            valid: false,
        };
    }
}

module.exports = CloneAction;

CloneAction.$inject = [
    "$teve.logger",
    "gameEditor.gameEditor",
    "$teve.ui.userFeedback",
    "$teve.entities.NetworkDiscoveryPlan",
    "$teve.userSession",
    "gameEditor.activeView",
];

CloneAction.$displayDetails = {
    type: "Action",
    name: "CloneAction",
    icon: "common.duplicate",
    title: "Clone Discovery Plan",
    shown: true,
    order: 4,
};
